import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import leadAutomationService from '../../services/leadAutomationService';
import CommercialPlaybookCard from './CommercialPlaybookCard';

const empty = {
  enabled: false,
  channels: ['whatsapp', 'instagram'],
  delay_min_seconds: 6,
  delay_max_seconds: 9,
  first_message_only: true,
  scheduling_flow_enabled: false,
  commercial_conditions: {
    payment_methods: [],
    installment_guidance: '',
  },
  max_automated_replies: 1,
  conversation_window_minutes: 1440,
  reply_cooldown_seconds: 6,
  handoff_pause_minutes: 1440,
  custom_handoff_keywords: [],
  send_fallback_message: true,
  fallback_message: 'Recebemos sua mensagem 😊 Nossa equipe continuará o atendimento assim que possível.',
  ai_enabled: false,
  ai_monthly_limit: 0,
  rules: [],
};

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 14,
  padding: 20,
  marginBottom: 16,
};

const assistantSections = [
  { key: 'general', label: 'Visão geral' },
  { key: 'playbook', label: 'Playbook comercial' },
  { key: 'clinical', label: 'Fluxo clínico' },
  { key: 'responses', label: 'Respostas e intenções' },
  { key: 'behavior', label: 'Limites e equipe' },
];

const normalizeRule = rule => {
  const storedResponses = Array.isArray(rule.responses)
    ? rule.responses.map(response => String(response || ''))
    : [];

  const responses = storedResponses.some(response => response.trim())
    ? storedResponses
    : [String(rule.response || '')];

  return {
    ...rule,
    response: String(rule.response || responses[0] || ''),
    responses: [0, 1, 2].map(index => responses[index] || ''),
  };
};

const normalizeForm = data => ({
  ...empty,
  ...data,

  scheduling_flow_enabled: Boolean(
    data?.scheduling_flow_enabled
  ),
  max_automated_replies: Math.min(
    4,
    Math.max(
      1,
      Number(
        data?.max_automated_replies
          ?? empty.max_automated_replies
      )
    )
  ),
  custom_handoff_keywords: Array.isArray(
    data?.custom_handoff_keywords
  )
    ? data.custom_handoff_keywords
    : [],
  commercial_conditions: {
    payment_methods: Array.isArray(
      data?.commercial_conditions?.payment_methods
    )
      ? data.commercial_conditions.payment_methods
      : [],
    installment_guidance: String(
      data?.commercial_conditions?.installment_guidance || ''
    ),
  },
  rules: (data?.rules || []).map(normalizeRule),
});

const paymentMethods = [
  ['pix', 'Pix'],
  ['dinheiro', 'Dinheiro'],
  ['cartao_credito', 'Cartão de crédito'],
  ['cartao_debito', 'Cartão de débito'],
  ['transferencia', 'Transferência'],
  ['boleto', 'Boleto'],
];

const CommercialConditions = ({ value, onToggle, onGuidanceChange }) => (
  <>
    <p className={'text-muted'}>Somente condições cadastradas serão informadas; valores dependem da avaliação.</p>
    <Form.Label>Formas de pagamento aceitas</Form.Label>
    {paymentMethods.map(([method, label]) => (
      <Form.Check key={method} label={label}
        checked={value.payment_methods.includes(method)}
        onChange={() => onToggle(method)} />
    ))}
    <Form.Group className={'mt-3'}>
      <Form.Label>Orientação sobre parcelamento</Form.Label>
      <Form.Control as={'textarea'} rows={2} maxLength={300}
        value={value.installment_guidance}
        placeholder={'Ex.: As opções são apresentadas após a avaliação.'}
        onChange={event => onGuidanceChange(event.target.value)} />
      <Form.Text muted>
        Não informe preços ou condições variáveis entre pacientes.
      </Form.Text>
    </Form.Group>
  </>
);

export default function LeadAutomationTab() {
  const [activeSection, setActiveSection] = useState('general');
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    leadAutomationService.get()
      .then(data => setForm(normalizeForm(data)))
      .catch(() => setNotice({
        type: 'danger',
        text: 'Não foi possível carregar a automação.',
      }))
      .finally(() => setLoading(false));
  }, []);

  const set = (name, value) => {
    setForm(current => ({ ...current, [name]: value }));
  };

  const toggleChannel = channel => {
    set(
      'channels',
      form.channels.includes(channel)
        ? form.channels.filter(item => item !== channel)
        : [...form.channels, channel]
    );
  };

  const togglePaymentMethod = method => {
    const methods = form.commercial_conditions.payment_methods || [];
    set('commercial_conditions', {
      ...form.commercial_conditions,
      payment_methods: methods.includes(method)
        ? methods.filter(item => item !== method)
        : [...methods, method],
    });
  };

  const updateRule = (index, patch) => {
    set(
      'rules',
      form.rules.map((rule, ruleIndex) => (
        ruleIndex === index ? { ...rule, ...patch } : rule
      ))
    );
  };

  const updateRuleResponse = (ruleIndex, responseIndex, value) => {
    const rule = form.rules[ruleIndex];
    const responses = [...(rule.responses || ['', '', ''])];

    responses[responseIndex] = value;

    updateRule(ruleIndex, {
      responses,
      response: responses.find(response => response.trim()) || '',
    });
  };

  const preparePayload = () => ({
    ...form,
    custom_handoff_keywords: (
    form.custom_handoff_keywords || []
  )
    .map(keyword => keyword.trim())
    .filter(Boolean)
    .filter(
      (keyword, index, keywords) =>
        keywords.indexOf(keyword) === index
    ),

  rules: form.rules.map(rule => {
      const responses = (rule.responses || [])
        .map(response => response.trim())
        .filter(Boolean)
        .filter((response, index, items) => items.indexOf(response) === index);

      return {
        ...rule,
        response: responses[0] || '',
        responses,
      };
    }),
  });

  const save = async () => {
    const payload = preparePayload();

    const ruleWithoutResponse = payload.rules.find(
      rule => rule.enabled && !rule.responses.length
    );

    if (ruleWithoutResponse) {
      setNotice({
        type: 'danger',
        text: `Informe ao menos uma resposta para a intenção "${ruleWithoutResponse.intent}".`,
      });
      return;
    }

    setSaving(true);
    setNotice(null);

    try {
      const updated = await leadAutomationService.update(payload);
      setForm(normalizeForm(updated));
      setNotice({
        type: 'success',
        text: 'Assistente de atendimento atualizado.',
      });
    } catch (error) {
      setNotice({
        type: 'danger',
        text: error.response?.data?.message || 'Falha ao salvar.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          background: 'linear-gradient(135deg,#1e3a5f,#7c3aed)',
          borderRadius: 14,
          padding: '20px 24px',
          color: '#fff',
          marginBottom: 20,
        }}
      >
        <h4 style={{ margin: 0, fontWeight: 800 }}>
          Assistente de Atendimento
        </h4>

        <div style={{ opacity: 0.8, fontSize: 13 }}>
          Regras locais ilimitadas, espera natural e transferência segura
          para a equipe.
        </div>
      </div>

      {notice && <Alert variant={notice.type}>{notice.text}</Alert>}

      <div className="d-flex flex-wrap gap-2 mb-4" role="tablist"
        aria-label="Seções do assistente">
        {assistantSections.map(section => (
          <Button key={section.key} type="button" role="tab" size="sm"
            variant={activeSection === section.key ? 'primary' : 'outline-secondary'}
            aria-selected={activeSection === section.key}
            onClick={() => setActiveSection(section.key)}>
            {section.label}
          </Button>
        ))}
      </div>

      {activeSection === 'playbook' && <CommercialPlaybookCard />}

      <div style={{ ...card, display: activeSection === 'general' ? undefined : 'none' }}>
        <Form.Check
          type="switch"
          label="Ativar respostas automáticas"
          checked={form.enabled}
          onChange={event => set('enabled', event.target.checked)}
        />

        <hr />

        <Form.Label>Canais</Form.Label>

        <div className="d-flex gap-4 mb-3">
          {['whatsapp', 'instagram'].map(channel => (
            <Form.Check
              key={channel}
              label={channel === 'whatsapp'
                ? 'WhatsApp'
                : 'Instagram Direct'}
              checked={form.channels.includes(channel)}
              onChange={() => toggleChannel(channel)}
            />
          ))}
        </div>

        <Form.Check
          type="switch"
          label="Automatizar somente a primeira resposta"
          checked={form.first_message_only}
          onChange={event => set(
            'first_message_only',
            event.target.checked
          )}
        />

        <hr />

        <Form.Group controlId="lead-automation-scheduling-flow">
          <Form.Check
            type="switch"
            label="Ativar fluxo contextual de agendamento"
            checked={Boolean(form.scheduling_flow_enabled)}
            onChange={event => set(
              'scheduling_flow_enabled',
              event.target.checked
            )}
          />

          <Form.Text muted>
            Quando ativado, o assistente coleta procedimento, unidade e
            preferência de dia ou período antes de transferir o atendimento
            para a equipe.
          </Form.Text>
        </Form.Group>
      </div>

      <div style={{ ...card, display: activeSection === 'behavior' ? undefined : 'none' }}>
        <h6>Limites e transferência humana</h6>

        <div className="text-muted mb-3" style={{ fontSize: 12 }}>
          Evita respostas excessivas e suspende a automação quando a conversa
          precisa ser assumida pela equipe.
        </div>

        <div className="row g-3">
          <Form.Group
            className="col-md-6"
            controlId="automation-max-replies"
          >
            <Form.Label>Máximo de respostas por conversa</Form.Label>

            <Form.Control
              type="number"
              min="1"
              max="4"
              disabled={form.first_message_only}
              value={
                form.first_message_only
                  ? 1
                  : form.max_automated_replies
              }
              onChange={event => set(
                'max_automated_replies',
                Number(event.target.value)
              )}
            />

            <Form.Text muted>
              Entre 1 e 4. A opção de primeira resposta sempre limita a uma.
            </Form.Text>
          </Form.Group>

          <Form.Group
            className="col-md-6"
            controlId="automation-conversation-window"
          >
            <Form.Label>Janela da conversa (horas)</Form.Label>

            <Form.Control
              type="number"
              min="1"
              max="168"
              value={Math.max(
                1,
                Math.round(
                  Number(form.conversation_window_minutes || 1440) / 60
                )
              )}
              onChange={event => set(
                'conversation_window_minutes',
                Number(event.target.value) * 60
              )}
            />

            <Form.Text muted>
              Depois desse período, uma nova conversa pode iniciar.
            </Form.Text>
          </Form.Group>

          <Form.Group
            className="col-md-6"
            controlId="automation-reply-cooldown"
          >
            <Form.Label>Intervalo mínimo entre respostas (segundos)</Form.Label>

            <Form.Control
              type="number"
              min="6"
              max="86400"
              value={form.reply_cooldown_seconds}
              onChange={event => set(
                'reply_cooldown_seconds',
                Number(event.target.value)
              )}
            />

            <Form.Text muted>
              O próximo envio será adiado quando necessário.
            </Form.Text>
          </Form.Group>

          <Form.Group
            className="col-md-6"
            controlId="automation-handoff-pause"
          >
            <Form.Label>Pausa após transferência (horas)</Form.Label>

            <Form.Control
              type="number"
              min="1"
              max="168"
              value={Math.max(
                1,
                Math.round(
                  Number(form.handoff_pause_minutes || 1440) / 60
                )
              )}
              onChange={event => set(
                'handoff_pause_minutes',
                Number(event.target.value) * 60
              )}
            />

            <Form.Text muted>
              Durante a pausa, o atendimento permanece com a equipe.
            </Form.Text>
          </Form.Group>
        </div>

        <hr />

        <Form.Group controlId="automation-custom-handoff-keywords">
          <Form.Label>
            Palavras adicionais para transferir ao humano
          </Form.Label>

          <Form.Control
            as="textarea"
            rows={2}
            value={(form.custom_handoff_keywords || []).join(', ')}
            placeholder="Ex.: reembolso, processo, falar com a gerente"
            onChange={event => set(
              'custom_handoff_keywords',
              event.target.value
                .split(',')
                .map(value => value.trim())
                .filter(Boolean)
            )}
          />

          <Form.Text muted>
            Separe palavras e expressões por vírgula.
          </Form.Text>
        </Form.Group>

        <Alert variant="info" className="mt-3 mb-0">
          Pedidos de atendente, reclamações, urgências e dúvidas sobre
          diagnóstico ou medicamentos sempre serão encaminhados à equipe.
        </Alert>
      </div>

      <div style={{ ...card, display: activeSection === 'clinical' ? undefined : 'none' }}>
        <h6>Condições comerciais</h6>
        <CommercialConditions
          value={form.commercial_conditions}
          onToggle={togglePaymentMethod}
          onGuidanceChange={value => set('commercial_conditions', {
            ...form.commercial_conditions,
            installment_guidance: value,
          })}
        />
      </div>

      <div style={{ ...card, display: activeSection === 'behavior' ? undefined : 'none' }}>
        <h6>Tempo de resposta</h6>

        <div className="row g-3">
          <Form.Group className="col-md-6">
            <Form.Label>Mínimo (segundos)</Form.Label>
            <Form.Control
              type="number"
              min="6"
              max="60"
              value={form.delay_min_seconds}
              onChange={event => set(
                'delay_min_seconds',
                Number(event.target.value)
              )}
            />
          </Form.Group>

          <Form.Group className="col-md-6">
            <Form.Label>Máximo (segundos)</Form.Label>
            <Form.Control
              type="number"
              min={form.delay_min_seconds}
              max={60} 
              value={form.delay_max_seconds}
              onChange={event => set(
                'delay_max_seconds',
                Number(event.target.value)
              )}
            />
          </Form.Group>
        </div>
      </div>

      <div style={{ ...card, display: activeSection === 'responses' ? undefined : 'none' }}>
        <h6>Intenções seguras</h6>

        <div className="text-muted mb-2" style={{ fontSize: 12 }}>
          O assistente escolherá uma das respostas aprovadas e evitará
          repetir a última variação enviada ao mesmo lead.
        </div>

        {form.rules.map((rule, index) => (
          <div
            key={`${rule.intent}-${index}`}
            style={{
              borderTop: index ? '1px solid #f1f5f9' : 0,
              padding: '14px 0',
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>{rule.intent}</strong>

              <Form.Check
                type="switch"
                checked={!!rule.enabled}
                onChange={event => updateRule(index, {
                  enabled: event.target.checked,
                })}
              />
            </div>

            <Form.Group className="mb-3">
              <Form.Label>
                Palavras e expressões (separadas por vírgula)
              </Form.Label>

              <Form.Control
                value={(rule.keywords || []).join(', ')}
                onChange={event => updateRule(index, {
                  keywords: event.target.value
                    .split(',')
                    .map(value => value.trim())
                    .filter(Boolean),
                })}
              />
            </Form.Group>

            {(rule.responses || ['', '', '']).map(
              (response, responseIndex) => (
                <Form.Group
                  className="mb-2"
                  key={`${rule.intent}-response-${responseIndex}`}
                  controlId={`lead-automation-${rule.intent}-response-${responseIndex}`}
                >
                  <Form.Label>
                    Resposta aprovada {responseIndex + 1}
                    {responseIndex === 0 ? ' (principal)' : ' (opcional)'}
                  </Form.Label>

                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={response}
                    placeholder={
                      responseIndex === 0
                        ? 'Informe a resposta principal.'
                        : 'Informe uma variação para tornar o atendimento mais natural.'
                    }
                    onChange={event => updateRuleResponse(
                      index,
                      responseIndex,
                      event.target.value
                    )}
                  />
                </Form.Group>
              )
            )}
          </div>
        ))}
      </div>

      <div style={{ ...card, display: activeSection === 'responses' ? undefined : 'none' }}>
        <Form.Check
          type="switch"
          label="Enviar confirmação quando nenhuma regra reconhecer a mensagem"
          checked={form.send_fallback_message}
          onChange={event => set(
            'send_fallback_message',
            event.target.checked
          )}
        />

        <Form.Control
          className="mt-3"
          as="textarea"
          rows={2}
          disabled={!form.send_fallback_message}
          value={form.fallback_message || ''}
          onChange={event => set(
            'fallback_message',
            event.target.value
          )}
        />

        <div className="text-muted mt-2" style={{ fontSize: 12 }}>
          A mensagem desconhecida continuará aguardando atendimento humano.
        </div>
      </div>

      <div style={{
        ...card,
        opacity: 0.75,
        display: activeSection === 'general' ? undefined : 'none',
      }}>
        <h6>IA — preparada para uma etapa futura</h6>

        <Form.Check
          type="switch"
          disabled
          label="Usar IA quando nenhuma regra reconhecer a mensagem"
          checked={false}
        />

        <div className="text-muted mt-2" style={{ fontSize: 12 }}>
          Nenhum provedor ou cobrança de IA é necessário neste MVP.
        </div>
      </div>

      <Button
        style={{ display: activeSection === 'playbook' ? 'none' : undefined }}
        onClick={save}
        disabled={saving || !form.channels.length}
      >
        {saving ? 'Salvando...' : 'Salvar configurações'}
      </Button>
    </div>
  );
}
